import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ai, board, check, solid, storage, team, workspace, } from '../assets/images/image';
import { AnimatePresence, motion } from "framer-motion";

const FEATURE_IDS = ['storage', 'team', 'board', 'workspace', 'ai'] as const;

const fadeUp = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
            delayChildren: 0.3,
        }
    }
};

const ICON_BY_FEATURE: Record<(typeof FEATURE_IDS)[number], string> = {
    storage,
    team,
    board,
    workspace,
    ai,
};

const Pricing: React.FC = () => {
    const { t } = useTranslation();
    const [billingPeriod, setBillingPeriod] = useState('annual');

    const { features, featureMB } = useMemo(() => {
        const rows = FEATURE_IDS.map((id) => {
            const base = `pricing.features.${id}`;
            const freemiumSub = t(`${base}.freemiumSubdesc`, { returnObjects: true }) as string[];
            const premiumSub = t(`${base}.premiumSubdesc`, { returnObjects: true }) as string[];
            return {
                icon: ICON_BY_FEATURE[id],
                title: t(`${base}.title`),
                freemium: {
                    desc: t(`${base}.freemiumDesc`),
                    subdesc: Array.isArray(freemiumSub) ? freemiumSub : [String(freemiumSub)],
                },
                premium: {
                    desc: t(`${base}.premiumDesc`),
                    subdesc: Array.isArray(premiumSub) ? premiumSub : [String(premiumSub)],
                },
            };
        });
        return {
            features: rows,
            featureMB: {
                freemiumFeatures: rows.map((v) => ({
                    icon: v.icon,
                    title: v.title,
                    desc: v.freemium.desc,
                    subdesc: v.freemium.subdesc,
                })),
                premiumFeatures: rows.map((v) => ({
                    icon: v.icon,
                    title: v.title,
                    desc: v.premium.desc,
                    subdesc: v.premium.subdesc,
                })),
            },
        };
    }, [t]);
    return (
        <>
            <div className="max-lg:px-4">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                    className="lg:flex flex-row w-full items-center lg:text-center text-[42px] lg:text-[65px] font-[274] text-[#494949] lg:mt-[60px] mt-[80px] item-center justify-center lg:space-x-5"
                >
                    <div className='inline-flex lg:h-[70px] h-[50px] justify-center items-center gap-4 shrink-0 lg:pb-1'>
                        {t('pricing.titleBefore')}
                    </div>
                    <div className="flex items-center max-lg:mt-2 max-lg:w-full max-lg:justify-start">
                        <div className='inline-flex h-[81px] justify-center items-center gap-4 shrink-0 text-white bg-[#3A57FF] px-3 max-lg:h-[64px] max-lg:px-6 max-lg:text-[42px] max-lg:font-black max-lg:leading-none max-lg:whitespace-nowrap'>
                            {t('pricing.titleHighlight')}
                        </div>
                        <div className="triangle-right max-lg:scale-[0.53] max-lg:origin-left max-lg:-ml-[1px]"></div>
                    </div>
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                    className="mt-8 w-full"
                >
                    <p className="lg:text-[26px] text-[16px] text-[#494949] leading-[normal] lg:text-center">
                        {t('pricing.subtitle')}
                    </p>
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={1}
                    className='lg:mt-16 mt-4'
                >
                    <div className="flex items-center justify-center">
                        <div className="inner-shadow py-1 px-2 rounded-[32px] bg-[#E8E8E8] flex space-x-4 max-lg:w-full">
                            <motion.div
                                onClick={() => setBillingPeriod('annual')}
                                whileTap={{ scale: 0.98 }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{
                                    opacity: billingPeriod === "annual" ? 1 : 0.7,
                                    y: 0,
                                    scale: billingPeriod === "annual" ? 1 : 0.98
                                }}
                                transition={{ duration: 0.3 }}
                                className={`cursor-pointer py-4 text-center text-xs md:text-sm rounded-3xl min-w-[167px] max-lg:flex-1 whitespace-nowrap ${billingPeriod === "annual" ? 'text-[white] bg-[#3A57FF] font-bold' : 'text-[#494949]'}`}
                            >
                                {t('pricing.annual')}
                            </motion.div>
                            <motion.div
                                onClick={() => setBillingPeriod('monthly')}
                                whileTap={{ scale: 0.98 }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{
                                    opacity: billingPeriod === "monthly" ? 1 : 0.7,
                                    y: 0,
                                    scale: billingPeriod === "monthly" ? 1 : 0.98
                                }}
                                transition={{ duration: 0.3 }}
                                className={`cursor-pointer py-4 text-center text-xs md:text-sm rounded-3xl min-w-[167px] max-lg:flex-1 whitespace-nowrap ${billingPeriod === "monthly" ? 'text-[white] bg-[#3A57FF] font-bold' : 'text-[#494949]'}`}
                            >
                                {t('pricing.monthly')}
                            </motion.div>
                        </div>
                    </div>
                    <div className="flex items-center justify-center gap-x-4 mt-4">
                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#00BA00]">
                            <img src={check} alt="check" />
                        </div>
                        <p className='text-base text-[#494949] font-medium'>{t('pricing.guarantee')}</p>
                    </div>
                    <div className="space-y-4 mt-8">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={billingPeriod}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="grid grid-cols-10 gap-x-[40px]">
                                    <div className="lg:col-start-3 lg:col-span-3 col-span-full lg:flex lg:items-center lg:justify-center">
                                        <div className="space-y-4 text-center lg:w-fit w-full">
                                            <div className="space-y-4">
                                                <p className='text-[42px] font-light text-[#494949]'>{t('pricing.freemiumName')}</p>
                                                <p className='text-base font-bold text-[#B1B1B1]'>{t('pricing.freePrice')}</p>
                                            </div>
                                            <div className='py-4 px-8 border border-[#3A57FF] rounded-2xl cursor-pointer' onClick={() => { window.location.href = 'https://app.uboards.ai/auth/login' }}>
                                                <p className='text-[17px] font-regular text-[#494949]'>{t('pricing.getStartedFree')}</p>
                                            </div>
                                            <p className="text-[17px] font-bold text-[#494949]">{t('pricing.keyFeatures')}</p>
                                        </div>
                                        <div className="mt-4 lg:hidden">
                                            {
                                                featureMB.freemiumFeatures.map((v, index) => {
                                                    return (
                                                        <motion.div
                                                            key={v.title}
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ duration: 0.3, delay: index * 0.1 }}
                                                            className="pt-4 border-t border-[#B1B1B1] grid grid-cols-12 space-y-4"
                                                        >
                                                            <div className="col-span-full flex items-center gap-x-3">
                                                                <div className="w-8 h-8 flex items-center justify-center">
                                                                    <img src={v.icon} alt={v.title} className='object-contain' />
                                                                </div>
                                                                <p className="text-base text-[#494949]">{v.title}</p>
                                                            </div>
                                                            <div className='col-span-full flex gap-x-4'>
                                                                <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full ${billingPeriod === 'annual' ? 'bg-[#00BA00]' : 'bg-[#3A57FF]'}`}>
                                                                    <img src={check} alt="check" />
                                                                </div>
                                                                <div className="space-y-4">
                                                                    <p className='text-base text-[#494949] font-bold'>{v.desc}</p>
                                                                    <ul className='list-disc list-inside text-base text-[#888888] italic'>
                                                                        {
                                                                            v.subdesc.map((sub, idx) => (
                                                                                <li key={idx} className='pl-2'>{sub}</li>
                                                                            ))
                                                                        }
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    )
                                                })
                                            }
                                        </div>
                                    </div>
                                    <div className="lg:col-span-3 col-span-full lg:flex items-center justify-center">
                                        <div className="space-y-4 text-center lg:w-fit w-full">
                                            <>
                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-x-4 justify-center">
                                                        <p className='text-[42px] font-light text-[#494949]'>{t('pricing.premiumName')}</p>
                                                        <div className={`p-4 text-[17px] font-bold text-white ${billingPeriod === 'annual' ? 'bg-[#00BA00]' : 'bg-[#3A57FF]'}`}>
                                                            {t('pricing.savePercent', { percent: billingPeriod === 'annual' ? 35 : 18 })}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-x-2 justify-center">
                                                        {billingPeriod === 'monthly' && (
                                                            <p className='text-[26px] font-bold text-[#B1B1B1] line-through'>$12</p>
                                                        )}
                                                        <p
                                                            className={`text-[26px] font-bold ${billingPeriod === 'annual' ? 'text-[#00BA00]' : 'text-[#3A57FF]'}`}
                                                        >
                                                            ${billingPeriod === 'annual' ? '7.8' : '9.9'}
                                                        </p>
                                                        <p className='text-base font-bold text-[#494949]'>
                                                            {t('pricing.perSeatMonth', {
                                                                period: billingPeriod === 'annual' ? t('pricing.billedAnnually') : t('pricing.billedMonthly'),
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div onClick={() => { window.location.href = 'https://app.uboards.ai/settings/billing' }} className={`py-4 px-8 rounded-2xl cursor-pointer flex items-center justify-center gap-x-2 ${billingPeriod === 'annual' ? 'bg-[#00BA00]' : 'bg-[#3A57FF]'}`}>
                                                    <p className='text-[17px] font-medium text-white'>
                                                        {billingPeriod === 'annual' ? t('pricing.upgradePremium') : t('pricing.selectPlan')}
                                                    </p>
                                                    <img src={solid} alt="solid" />
                                                </div>
                                            </>
                                            <p className="text-[17px] font-bold text-[#494949]">{t('pricing.keyFeatures')}</p>
                                        </div>
                                        <div className="mt-4 lg:hidden">
                                            {
                                                featureMB.premiumFeatures.map((v, index) => {
                                                    return (
                                                        <motion.div
                                                            key={v.title}
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ duration: 0.3, delay: index * 0.1 }}
                                                            className="pt-4 border-t border-[#B1B1B1] grid grid-cols-12 space-y-4"
                                                        >
                                                            <div className="col-span-full flex items-center gap-x-3">
                                                                <div className="w-8 h-8 flex items-center justify-center">
                                                                    <img src={v.icon} alt={v.title} className='object-contain' />
                                                                </div>
                                                                <p className="text-base text-[#494949]">{v.title}</p>
                                                            </div>
                                                            <div className='col-span-full flex gap-x-4'>
                                                                <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full ${billingPeriod === 'annual' ? 'bg-[#00BA00]' : 'bg-[#3A57FF]'}`}>
                                                                    <img src={check} alt="check" />
                                                                </div>
                                                                <div className="space-y-4">
                                                                    <p className='text-base text-[#494949] font-bold'>{v.desc}</p>
                                                                    <ul className='list-disc list-inside text-base text-[#888888] italic'>
                                                                        {
                                                                            v.subdesc.map((sub, idx) => (
                                                                                <li key={idx} className='pl-2'>{sub}</li>
                                                                            ))
                                                                        }
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    )
                                                })
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-4 mt-7 max-lg:hidden">
                                    {
                                        features.map((v, index) => {
                                            return (
                                                <motion.div
                                                    key={v.title}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                                    className="pt-4 border-t border-[#B1B1B1] grid grid-cols-10 gap-x-[20px]"
                                                >
                                                    <div className="col-span-2 flex items-center gap-x-3">
                                                        <div className="w-8 h-8 flex items-center justify-center">
                                                            <img src={v.icon} alt={v.title} className='object-contain' />
                                                        </div>
                                                        <p className="text-base text-[#494949]">{v.title}</p>
                                                    </div>
                                                    <div className='col-span-4 flex gap-x-4'>
                                                        <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full ${billingPeriod === 'annual' ? 'bg-[#00BA00]' : 'bg-[#3A57FF]'}`}>
                                                            <img src={check} alt="check" />
                                                        </div>
                                                        <div className="space-y-4">
                                                            <p className='text-base text-[#494949] font-bold'>{v.freemium.desc}</p>
                                                            <ul className='list-disc list-inside text-base text-[#888888] italic'>
                                                                {
                                                                    v.freemium.subdesc.map((sub, idx) => (
                                                                        <li key={idx} className='pl-2'>{sub}</li>
                                                                    ))
                                                                }
                                                            </ul>
                                                        </div>
                                                    </div>
                                                    <div className='col-span-4 flex gap-x-4'>
                                                        <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full ${billingPeriod === 'annual' ? 'bg-[#00BA00]' : 'bg-[#3A57FF]'}`}>
                                                            <img src={check} alt="check" />
                                                        </div>
                                                        <div className="space-y-4">
                                                            <p className='text-base text-[#494949] font-bold'>{v.premium.desc}</p>
                                                            <ul className='list-disc list-inside text-base text-[#888888] italic'>
                                                                {
                                                                    v.premium.subdesc.map((sub, idx) => (
                                                                        <li key={idx} className='pl-2'>{sub}</li>
                                                                    ))
                                                                }
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )
                                        })
                                    }
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>
        </>
    );
};

export default Pricing;
